import localforage from 'localforage';

/**
 * Traza GPS local durable (2026-07-23, plan `2026-07-23-rv-mapa-gps-tiempo-real`
 * Task A1) — cada punto capturado por `useSurveillanceRouteGpsTracking` se
 * escribe AQUÍ (IndexedDB, instancia dedicada) en el momento de la lectura,
 * antes de intentar el envío al backend. Reemplaza el parche localStorage
 * `sr-gps-pending-*` (persistencia solo del final-flush fallido): con el
 * store como fuente única desde la captura, ningún punto depende ya de que
 * un flush concreto tenga éxito para sobrevivir a un cierre de pestaña/app.
 *
 * Clave por `routeId`; valor = `LocalGpsPoint[]` en orden cronológico.
 * `synced` marca los puntos ya confirmados por el backend (`AddGpsPointsBatch`
 * resuelto sin throw) — el hook de tracking solo reenvía los `!synced`.
 */

export type LocalGpsPoint = {
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  accuracy?: number;
  /**
   * Rumbo que reporta el dispositivo, en grados. Opcional y a menudo ausente:
   * con antena única llega `null` en muchos equipos y a baja velocidad es
   * ruido. Se guarda porque el detector de cambio de sentido lo usa como
   * señal S3 —solo descuenta metros de evidencia, nunca decide— y al pasar a
   * alimentarse de este almacén se habría perdido en silencio, endureciendo
   * el umbral sin que nadie lo notara.
   */
  heading?: number;
  synced?: boolean;
};

/** Oyente de puntos nuevos. Recibe SOLO lo que acaba de entrar, ordenado. */
export type GpsTrackListener = (points: LocalGpsPoint[]) => void;

const store = localforage.createInstance({
  name: 'rois',
  storeName: 'gps_local_track',
});
const MAX_POINTS = 20_000;

// Mutex lock to prevent race conditions during rapid async read-modify-write
// updates on the same localforage key. Mirrors the pattern in
// `src/offline/offlineQueue.ts` (`withLock`) — that helper is not exported,
// so it's replicated here rather than imported. Without this, a
// `markPointsSynced`/`clearLocalTrack` write can interleave with a
// concurrent `appendGpsPoints` and lost-update a freshly-captured GPS point.
let trackLock: Promise<void> = Promise.resolve();

async function withLock<T>(action: () => Promise<T>): Promise<T> {
  const nextLock = trackLock.then(() => action()).catch((err) => {
    console.error('Error in gps track store lock:', err);
    throw err;
  });
  trackLock = nextLock.then(() => { }).catch(() => { });
  return nextLock;
}

/**
 * Oyentes por recorrido.
 *
 * 🔴 Existe para que el detector de cambio de sentido deje de depender de la
 * pantalla del mapa. Los puntos entran por DOS puertas —el `watch` de la
 * pantalla y el drenaje del buffer nativo— y las dos pasan por
 * `appendGpsPoints`: suscribirse aquí es la única forma de verlos todos sin
 * releer IndexedDB cada pocos segundos, que a 20.000 puntos no es gratis.
 */
const listeners = new Map<string, Set<GpsTrackListener>>();

export function subscribeToGpsTrack(
  routeId: string,
  listener: GpsTrackListener
): () => void {
  const forRoute = listeners.get(routeId) ?? new Set<GpsTrackListener>();
  forRoute.add(listener);
  listeners.set(routeId, forRoute);
  return () => {
    forRoute.delete(listener);
    if (forRoute.size === 0) listeners.delete(routeId);
  };
}

function notifyTrackListeners(routeId: string, added: LocalGpsPoint[]): void {
  if (added.length === 0) return;
  const forRoute = listeners.get(routeId);
  if (!forRoute || forRoute.size === 0) return;
  for (const listener of forRoute) {
    try {
      listener(added);
    } catch (err) {
      // Un oyente que revienta no puede tumbar la escritura de la traza: el
      // dato es lo que hay que salvar, la reacción es accesoria.
      console.error('[SR-GPS] listener de la traza local ha fallado', err);
    }
  }
}

export async function appendGpsPoints(
  routeId: string,
  points: LocalGpsPoint[]
): Promise<void> {
  if (!routeId || points.length === 0) return;
  const added = await withLock(async () => {
    const prev = (await store.getItem<LocalGpsPoint[]>(routeId)) ?? [];
    const seen = new Set(prev.map((p) => p.timestamp));
    const merged = [...prev];
    const justAdded: LocalGpsPoint[] = [];
    for (const p of points) {
      if (!seen.has(p.timestamp)) {
        seen.add(p.timestamp);
        merged.push(p);
        justAdded.push(p);
      }
    }
    merged.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    await store.setItem(routeId, merged.slice(-MAX_POINTS));
    // 🔴 Ordenados: el núcleo del detector consume la traza como una serie
    // temporal y un lote desordenado le haría medir retrocesos que no existen.
    // El drenaje nativo no garantiza el orden de llegada.
    return justAdded.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  });
  // Fuera del lock: un oyente lento no puede bloquear la siguiente escritura.
  notifyTrackListeners(routeId, added);
}

export async function getLocalTrack(routeId: string): Promise<LocalGpsPoint[]> {
  return (await store.getItem<LocalGpsPoint[]>(routeId)) ?? [];
}

export async function getUnsyncedPoints(
  routeId: string
): Promise<LocalGpsPoint[]> {
  return (await getLocalTrack(routeId)).filter((p) => !p.synced);
}

export async function markPointsSynced(
  routeId: string,
  timestamps: string[]
): Promise<void> {
  if (timestamps.length === 0) return;
  return withLock(async () => {
    const ts = new Set(timestamps);
    const track = await getLocalTrack(routeId);
    await store.setItem(
      routeId,
      track.map((p) => (ts.has(p.timestamp) ? { ...p, synced: true } : p))
    );
  });
}

export async function clearLocalTrack(routeId: string): Promise<void> {
  return withLock(async () => {
    await store.removeItem(routeId);
  });
}

/**
 * Borra las trazas de TODOS los recorridos.
 *
 * Para el logout: los puntos GPS son de los recorridos del usuario anterior y
 * en una tablet compartida no deben quedar al alcance del siguiente. Además
 * crecen sin techo — nadie las borraba, porque `clearLocalTrack` necesita un
 * `routeId` concreto y el logout no tiene ninguno a mano.
 */
export async function clearAllLocalGpsTracks(): Promise<void> {
  return withLock(async () => {
    await store.clear();
  });
}

/**
 * Al cerrar sesión (spec 2026-10-12): borra las rutas cuyos puntos ya subieron
 * todos y CONSERVA enteras las que tienen alguno sin subir — son trabajo de
 * campo sin enviar, y el seguimiento las reenviará cuando vuelva a esa ruta.
 */
export async function pruneSyncedGpsTracks(): Promise<void> {
  return withLock(async () => {
    const routeIds = await store.keys();
    for (const routeId of routeIds) {
      const track = (await store.getItem<LocalGpsPoint[]>(routeId)) ?? [];
      if (track.every((point) => point.synced)) await store.removeItem(routeId);
    }
  });
}
